import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { homeAPI, socialAPI, sponsorshipAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ListingCard from "@/components/ListingCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TrendingUp, ArrowRight, Flag, Flame, MessagesSquare, Eye, Heart,
  Youtube, Instagram, Twitter, Facebook, Newspaper, ExternalLink, Megaphone, Users,
  Trophy as TrophyIcon, Image as ImageIcon,
} from "lucide-react";

const HERO_IMAGE_URL = "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/f4f9f2e529e8d26c_PLSR%20FRS%20Lite%20Chassis%20-%20SRC%20Home%20BG%20%28resize%203%29.webp";

function extractYouTubeId(url = "") {
  const m = url.match(/(?:v=|\/embed\/|youtu\.be\/|\/shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function sourceMeta(type) {
  switch (type) {
    case "youtube": return { icon: Youtube, color: "#FF0000", label: "YouTube" };
    case "instagram": return { icon: Instagram, color: "#E4405F", label: "Instagram" };
    case "twitter": return { icon: Twitter, color: "#000000", label: "X" };
    case "facebook": return { icon: Facebook, color: "#1877F2", label: "Facebook" };
    default: return { icon: Newspaper, color: "hsl(2 100% 55%)", label: "News" };
  }
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex items-end justify-between">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 h-9 w-1.5 flex-shrink-0 rounded-sm bg-primary sm:h-10" aria-hidden="true" />
        <div>
          <h2 className="font-display text-2xl font-bold uppercase leading-tight tracking-tight sm:text-3xl">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {action && (
        <Link to={action.to} className="text-xs font-semibold text-primary hover:underline">
          {action.label}
        </Link>
      )}
    </div>
  );
}

function CuratedCard({ item }) {
  const { icon: Icon, color, label } = sourceMeta(item.content_type);
  const videoId = item.content_type === "youtube" ? (item.video_id || extractYouTubeId(item.content_url || "")) : null;
  const openExternal = () => item.content_url && window.open(item.content_url, "_blank", "noopener,noreferrer");
  return (
    <div className="group flex w-72 flex-shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary/50" data-testid={`home-curated-${item.id}`}>
      {videoId ? (
        <div className="relative aspect-video w-full overflow-hidden bg-black">
          <iframe src={`https://www.youtube.com/embed/${videoId}`} title={item.title} className="h-full w-full" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : (
        <button onClick={openExternal} className="relative block aspect-video w-full overflow-hidden bg-secondary" type="button">
          {item.thumbnail_url ? (
            <img src={item.thumbnail_url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center" style={{ background: `${color}20` }}>
              <Icon className="h-10 w-10" style={{ color }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/10 to-transparent" />
        </button>
      )}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: color }}>
            <Icon className="h-3 w-3" /> {label}
          </span>
          {item.pinned && <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">Pinned</span>}
        </div>
        <button onClick={openExternal} type="button" className="text-left">
          <h4 className="line-clamp-2 text-sm font-semibold group-hover:text-primary">{item.title}</h4>
        </button>
        <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
          <span className="truncate">{item.source_name}</span>
          <button onClick={openExternal} type="button" className="flex items-center gap-1 hover:text-foreground">
            Open <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ForumRow({ post, hot }) {
  return (
    <Link to={`/forum/${post.id}`} data-testid={`home-forum-${post.id}`}>
      <Card className="border-border bg-card p-4 transition-colors hover:border-primary/50">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          {hot && (
            <span className="inline-flex items-center gap-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
              <Flame className="h-3 w-3" /> Hot
            </span>
          )}
          <Badge variant="secondary" className="text-[10px] capitalize">{(post.category || "general").replace("_", " ")}</Badge>
          {post.images?.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid={`home-forum-photos-${post.id}`}>
              <ImageIcon className="h-3 w-3" /> Photos
            </span>
          )}
        </div>
        <h3 className="line-clamp-1 text-sm font-medium">{post.title}</h3>
        <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
          <span>by {post.author_username}</span>
          <span className="flex items-center gap-1"><MessagesSquare className="h-3.5 w-3.5" /> {post.reply_count || 0}</span>
          <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {post.views || 0}</span>
        </div>
      </Card>
    </Link>
  );
}

function SocialRow({ post }) {
  const isAchievement = post.post_type === "achievement" || post.post_type === "simhub_milestone";
  const isSimhub = post.post_type === "simhub_milestone";
  const typeLabel = isSimhub ? "Achievement" : post.post_type === "achievement" ? "Achievement" : null;
  const hasImage = post.images?.length > 0;
  return (
    <Link to={`/social?post=${post.id}`} data-testid={`home-social-${post.id}`} className="block h-full">
      <Card className="flex h-full flex-col overflow-hidden border-border bg-card transition-colors hover:border-primary/40">
        {hasImage && (
          <div className="aspect-[4/3] w-full overflow-hidden bg-secondary">
            <img
              src={post.images[0]}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div className="flex flex-1 items-start gap-3 p-4">
          <Avatar className="h-9 w-9">
            <AvatarImage src={post.author_image} />
            <AvatarFallback className="bg-secondary text-xs">{(post.author_username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold">{post.author_username}</p>
              {typeLabel && (
                <Badge className="gap-1 border-0 bg-primary/20 text-[10px] font-semibold text-primary">
                  <TrophyIcon className="h-3 w-3" /> {typeLabel}
                </Badge>
              )}
            </div>
            {isAchievement && (post.milestone_badge || post.achievement_name) && (
              <div className="mt-2 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 p-2">
                <TrophyIcon className="h-4 w-4 flex-shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">{post.milestone_badge || post.achievement_name}</p>
                  {isSimhub && (
                    <p className="text-[9px] font-bold uppercase tracking-wide text-primary/80">SimHub verified</p>
                  )}
                </div>
              </div>
            )}
            {post.content && <p className="mt-2 line-clamp-3 text-sm text-foreground/90">{post.content}</p>}
            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {post.likes?.length || 0}</span>
              <span className="flex items-center gap-1"><MessagesSquare className="h-3.5 w-3.5" /> {post.comment_count || 0}</span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [forumHot, setForumHot] = useState([]);
  const [curated, setCurated] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [showSponsor, setShowSponsor] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sponsorshipAPI
      .getRequests()
      .then((res) => {
        const requests = Array.isArray(res.data) ? res.data : res.data?.requests || [];
        const blockingStatuses = ["pending", "deal_offered", "approved", "active"];
        const threeMonthsMs = 1000 * 60 * 60 * 24 * 91;
        const hasBlocking = requests.some((r) => {
          if (blockingStatuses.includes(r.status)) return true;
          if (r.status === "rejected") {
            const when = new Date(r.updated_at || r.created_at || 0).getTime();
            return Date.now() - when < threeMonthsMs;
          }
          return false;
        });
        setShowSponsor(!hasBlocking);
      })
      .catch(() => setShowSponsor(true));
  }, []);

  useEffect(() => {
    Promise.all([
      homeAPI.getTrendingListings(8).catch(() => ({ data: [] })),
      homeAPI.getPopularForumPosts(4).catch(() => ({ data: [] })),
      homeAPI.getCuratedFeed(12).catch(() => ({ data: [] })),
      homeAPI.getAnnouncements(3).catch(() => ({ data: [] })),
      socialAPI.getFeed({ limit: 3 }).catch(() => ({ data: { posts: [] } })),
    ])
      .then(([t, f, c, a, s]) => {
        setTrending(Array.isArray(t.data) ? t.data : t.data?.listings || []);
        setForumHot((Array.isArray(f.data) ? f.data : f.data?.posts || []).filter((x) => (x.status || "active") === "active"));
        setCurated(Array.isArray(c.data) ? c.data : c.data?.items || []);
        setAnnouncements(Array.isArray(a.data) ? a.data : a.data?.announcements || []);
        setRecentPosts(s.data?.posts || []);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-14">
      {/* Hero */}
      <section className="chrome-dark relative overflow-hidden rounded-2xl border border-border bg-background">
        <div
          className="absolute inset-0 opacity-60"
          style={{ backgroundImage: `url(${HERO_IMAGE_URL})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "right center" }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(240 8% 4%) 0%, hsl(240 8% 4% / 0.78) 60%, hsl(240 8% 4% / 0.18) 100%)",
          }}
        />
        <div className="relative p-8 sm:p-14">
          <div className="max-w-2xl animate-fade-up">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-5 w-1 rounded-sm bg-primary" aria-hidden="true" />
              <p className="text-xs font-bold uppercase italic tracking-widest text-foreground/85">Welcome Back</p>
            </div>
            <h1 className="font-display text-4xl font-bold uppercase leading-tight tracking-tight sm:text-6xl">
              {(user?.first_name || user?.username || "").split(" ")[0]}
            </h1>
            <p className="mt-3 max-w-lg text-muted-foreground">Buy and sell gear, talk setups on the forum, and share your best laps — all in one place.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/marketplace")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="home-browse-button">
                Browse Marketplace <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => navigate("/marketplace/create")} variant="outline" data-testid="home-sell-button">
                Sell an item
              </Button>
            </div>
          </div>
        </div>
        <div className="racing-line absolute bottom-0 left-0 right-0" />
      </section>

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className="border-t border-border/50 pt-10">
          <SectionHeader title="Announcements" subtitle="Latest updates" />
          <div className="space-y-2">
            {announcements.map((a) => (
              <Card
                key={a.id}
                className="flex items-start gap-3 border-border bg-card p-4"
                data-testid={`home-announcement-${a.id}`}
                onClick={() => a.link_url && window.open(a.link_url, "_blank", "noopener,noreferrer")}
                role={a.link_url ? "button" : undefined}
              >
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <Megaphone className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{a.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{a.content}</p>
                  {a.link_text && a.link_url && (
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary">{a.link_text} <ExternalLink className="h-3 w-3" /></span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Sponsorship callout */}
      {showSponsor && (
      <section className="border-t border-border/50 pt-10">
        <Card onClick={() => navigate("/sponsorship")} className="cursor-pointer overflow-hidden border-border bg-card p-0 transition-colors hover:border-primary/40" data-testid="home-sponsorship-card">
          <div className="grid gap-0 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <div className="p-6 sm:p-8">
              <h2 className="font-display text-2xl font-bold uppercase leading-tight">Launch and grow your sim racing <span className="text-primary">career</span></h2>
              <p className="mt-2 text-sm text-muted-foreground">From early backing to getting paid to race to finding your way into a real car, apply for sponsorship to get started.</p>
              <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="home-sponsorship-cta">Open sponsorship portal <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
            <div className="min-h-[160px] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1541446804404-9ab0f7dc12ef?q=80&w=1200&auto=format&fit=crop)" }} />
          </div>
        </Card>
      </section>
      )}

      {/* Hot Deals */}
      <section className="border-t border-border/50 pt-10">
        <SectionHeader title="Hot Deals" subtitle="Trending in marketplace" action={{ to: "/marketplace", label: "VIEW ALL" }} />
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-lg" />)}
          </div>
        ) : trending.length ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {trending.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No listings yet.</p>}
      </section>

      {/* Discussions */}
      <section className="border-t border-border/50 pt-10">
        <SectionHeader title="Discussions" subtitle="Popular forum topics" action={{ to: "/forum", label: "VIEW ALL" }} />
        {loading ? (
          <div className="grid gap-3 md:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
        ) : forumHot.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {forumHot.slice(0, 4).map((p, i) => <ForumRow key={p.id} post={p} hot={i === 0} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No discussions yet — start one!</p>}
      </section>

      {/* News & Hot Content */}
      <section className="border-t border-border/50 pt-10">
        <SectionHeader title="News & Hot Content" subtitle="Curated by the SRC team" />
        {loading ? (
          <div className="flex gap-4 overflow-x-auto pb-1">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-72 w-72 flex-shrink-0 rounded-lg" />)}
          </div>
        ) : curated.length ? (
          <div className="flex gap-4 overflow-x-auto pb-2 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
            {curated.map((item) => <CuratedCard key={item.id} item={item} />)}
          </div>
        ) : <p className="text-sm text-muted-foreground">No curated content yet.</p>}
      </section>

      {/* Paddock Banter */}
      <section className="border-t border-border/50 pt-10">
        <SectionHeader
          title="Paddock Banter"
          subtitle="Recent activity"
          action={{ to: "/social", label: "VIEW ALL" }}
        />
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
        ) : recentPosts.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {recentPosts.map((p) => <SocialRow key={p.id} post={p} />)}
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
            <Users className="h-4 w-4" /> No activity yet — post something in the Sim Racing Network.
          </div>
        )}
      </section>
    </div>
  );
}
